import { Button, Flex, Input, Radio, Space, Spin } from "antd"
import PropTypes from "prop-types"
import { colors, spacings } from "themes/variables"
import { Modal } from "ui/Modal"
import { Text } from "ui/Typography"
import { useTranslation } from "react-i18next"
import { useState, useCallback, useEffect } from "react"
import styles from "./index.module.css"
import CustomDrawer from "ui/Drawer"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import { useSelector, useDispatch } from "react-redux"
import { LoadingOutlined } from "@ant-design/icons"
import { createRefund, getClientBookingDetails } from "store/bookings"
import { toaster } from "components/common/Toast"
import { numberToCurrency } from "helpers/string"

const RefundModal = ({ onOpen = false, onClose, bookingUUID }) => {
  const { t } = useTranslation()
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm", "md"].includes(breakpoint)
  const { loading: refundLoading } = useSelector((state) => state.bookings.refund)
  const { data, loading } = useSelector((state) => state.bookings.refundDetails)
  const { total_paid = 0, non_refundable_amount = 0, refundable_amount = 0, card_total, cash_incomes, card_incomes } = data || {}

  const formattedTotalPaid = numberToCurrency(total_paid)
  const formattedNonRefundableDeposit = numberToCurrency(non_refundable_amount)
  const formattedRefundableAmount = numberToCurrency(refundable_amount)

  const [paymentType, setPaymentType] = useState("cash")
  const [refundAmount, setRefundAmount] = useState("")
  const [refundError, setRefundError] = useState("")
  const dispatch = useDispatch()

  useEffect(() => {
    if (cash_incomes?.length > 0) {
      setPaymentType("cash")
    } else if (card_incomes?.length > 0) {
      setPaymentType("card")
    }
  }, [cash_incomes, card_incomes])

  const validateRefundAmount = useCallback(
    (value, paymentType = "cash") => {
      if (value === "" || value === null) {
        return t("client.payment.refundAmountRequired")
      }
      if (isNaN(value) || Number(value) <= 0) {
        return t("client.payment.refundAmountGreaterThanZero")
      }
      if (paymentType === "card" && Number(value) > card_total) {
        return t("client.payment.refundAmountCannotExceedCard", { amount: card_total })
      }
      if (Number(value) > refundable_amount) {
        return t("client.payment.refundAmountCannotExceed", { amount: refundable_amount })
      }
      return ""
    },
    [card_total, refundable_amount, t]
  )

  const handleRefundChange = (e) => {
    const value = e.target.value
    setRefundAmount(value)
    setRefundError(validateRefundAmount(value, paymentType))
  }

  const handlePaymentTypeChange = (e) => {
    const value = e.target?.value
    setPaymentType(value)
    setRefundError(validateRefundAmount(refundAmount, value))
  }

  const handleSubmit = async () => {
    const error = validateRefundAmount(refundAmount, paymentType)
    setRefundError(error)
    if (error) return
    try {
      const response = await dispatch(createRefund(bookingUUID, { amount: refundAmount, stripe: paymentType === "card"}))
      toaster.success(response?.message)
      dispatch(getClientBookingDetails(bookingUUID))
    } catch (error) {
      console.error(error)
    } finally {
      onClose()
    }
  }

  const modalContent = (
    <Spin spinning={loading || refundLoading} indicator={<LoadingOutlined spin />} size="large">
      <Space vertical size={24} className="w-full" direction="vertical">
        <Space className={`w-full ${styles.amountContainer}`} size={spacings["4XL"]}>
          <Flex gap={spacings.S} className="py-xs px-s">
            <Text color={colors.textSecondaryDark}>{t("client.payment.totalPaid")}</Text>
            <Text color={colors.textPrimary}>{formattedTotalPaid}</Text>
          </Flex>
          <Flex gap={spacings.S} className="py-xs px-s">
            <Text color={colors.textSecondaryDark}>{t("client.payment.nonRefundableDeposit")}</Text>
            <Text color={colors.textPrimary}>{formattedNonRefundableDeposit}</Text>
          </Flex>
          <Flex gap={spacings.S} className="py-xs px-s">
            <Text color={colors.textSecondaryDark}>{t("client.payment.refundableAmount")}</Text>
            <Text color={colors.textPrimary}>{formattedRefundableAmount}</Text>
          </Flex>
        </Space>
        <Radio.Group value={paymentType} onChange={handlePaymentTypeChange}>
          <Space direction="horizontal" wrap>
            <Radio value="cash" disabled={cash_incomes?.length === 0}>
              {t("client.payment.inCash")}
            </Radio>
            <Radio value="card" disabled={card_incomes?.length === 0}>
              {t("client.payment.toCard")}
            </Radio>
          </Space>
        </Radio.Group>
        {paymentType === "card" && <Text>{t("client.payment.refundCardNote")}</Text>}
        <Flex vertical gap={spacings.XXS}>
          <Text>{t("client.payment.refundAmountLabel")}</Text>
          <Input
            value={refundAmount}
            prefix="$"
            type="number"
            min={0}
            max={formattedRefundableAmount}
            step="0.01"
            onChange={handleRefundChange}
            status={refundError ? "error" : ""}
            placeholder="Enter refund amount"
          />
          {refundError && <Text color={colors.error}>{refundError}</Text>}
        </Flex>
        {paymentType === "card" && (
          <Flex vertical gap={spacings.S} style={{ backgroundColor: colors.surface1 }} className="p-s">
            <Text fw={500}>
              {t("client.payment.totalRefund")} {formattedRefundableAmount}
            </Text>
            {card_incomes?.map((card, idx) => {
              const { id, type, method, last4, amount } = card || {}
              return (
                <Flex gap={spacings.XS} key={id}>
                  <Text>
                    {type || ""} {idx + 1} ({method || ""} **** {last4 || ""})
                  </Text>
                  <Text fw={500}>{numberToCurrency(amount)}</Text>
                </Flex>
              )
            })}
          </Flex>
        )}
        <Flex gap={spacings.S}>
          <Button className="py-l px-xl" color="primary" variant="outlined" onClick={onClose}>
            {t("global.cancel")}
          </Button>
          <Button type="primary" className="py-l px-xl" disabled={!!refundError || !refundAmount} onClick={handleSubmit}>
            {t("global.submit")}
          </Button>
        </Flex>
      </Space>
    </Spin>
  )

  return isMobile ? (
    <CustomDrawer open={onOpen} setOpen={onClose} title={t("client.payment.refund")}>
      {modalContent}
    </CustomDrawer>
  ) : (
    <Modal centered title={t("client.payment.refund")} open={onOpen} onOk={onClose} onCancel={onClose} width={800}>
      {modalContent}
    </Modal>
  )
}

RefundModal.propTypes = {
  onOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  bookingUUID: PropTypes.string.isRequired
}

export default RefundModal
